import React from 'react';
import { HiUserPlus } from 'react-icons/hi2';
import { BaseModal } from '../../../../components/Modal/BaseModal';
import { Button } from '../../../../components/Button';
import { UserSetorSelect } from '../../../../components/Select/UserSetorSelect';

export interface UserOption {
  value: number;
  label: string;
  name: string;
  email: string;
  avatar?: string;
}

export interface AssignDemandModalProps {
  isOpen: boolean;
  isMinimized: boolean;
  isLoading: boolean;
  selectedUserId: number | null;
  assignComment: string;
  userOptions: UserOption[];
  onMinimize: () => void;
  onRestore: () => void;
  onClose: () => void;
  onUserChange: (userId: number | null) => void;
  onCommentChange: (comment: string) => void;
  onConfirm: () => void;
}

/**
 * Modal para atribuir demanda a um usuário
 */
export const AssignDemandModal: React.FC<AssignDemandModalProps> = ({
  isOpen,
  isMinimized,
  isLoading,
  selectedUserId,
  assignComment,
  userOptions,
  onMinimize,
  onRestore,
  onClose,
  onUserChange,
  onCommentChange,
  onConfirm,
}) => {
  return (
    <BaseModal
      isOpen={isOpen}
      isMinimized={isMinimized}
      modalId="demand-assign"
      actionIcon={<HiUserPlus className="w-4 h-4" />}
      toolbarActionId="assignTo"
      onMinimize={onMinimize}
      onRestore={onRestore}
      onClose={onClose}
      title="Atribuir Demanda"
      description="Selecione um usuário para atribuir esta demanda. O usuário será notificado sobre a nova atribuição."
      useStandardLayout={true}
      size="md"
      resizable={false}
      stickyFooter={true}
      footerContent={
        <div className="flex justify-end gap-3 p-4">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="min-w-20"
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={!selectedUserId || isLoading}
            isLoading={isLoading}
            className="min-w-20"
          >
            {isLoading ? 'Atribuindo...' : 'Atribuir'}
          </Button>
        </div>
      }
    >
      <div className="p-4 space-y-4">
        {/* Centered Message */}
        <div className="text-center text-sm text-gray-900 dark:text-gray-100 mb-4">
          Selecione o usuário para quem deseja atribuir esta demanda.
        </div>

        {/* Seleção de usuário */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Usuário <span className="text-red-500">*</span>
          </label>
          <UserSetorSelect
            options={userOptions}
            value={selectedUserId ?? ''}
            variant="user"
            onChange={(value) => onUserChange(Number(value))}
          />
        </div>

        {/* Comentário opcional */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Comentário (opcional)
          </label>
          <textarea
            value={assignComment}
            onChange={(e) => onCommentChange(e.target.value)}
            placeholder="Digite um comentário sobre a atribuição..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-md 
              bg-white dark:bg-neutral-800 text-gray-900 dark:text-gray-100
              focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
              resize-none transition-colors"
          />
        </div>
      </div>
    </BaseModal>
  );
};
